export type SiteDisarmForOption = 'until-next-schedule' | 'fixed-time' | 'date-time';

/** Max horizon for Date & Time and custom fixed-time disarm (from now). */
export const SITE_DISARM_DATE_TIME_MAX_HOURS = 48;

/** Custom / Date & Time cap in minutes (48 hours). */
export const SITE_DISARM_FIXED_DURATION_MAX_MINUTES = SITE_DISARM_DATE_TIME_MAX_HOURS * 60;

/** Preset chips for Fixed Time Period. */
export const SITE_DISARM_FIXED_DURATION_PRESET_MINUTES = [5, 10, 30, 60] as const;

export type SiteDisarmFixedDurationPresetMinutes =
  (typeof SITE_DISARM_FIXED_DURATION_PRESET_MINUTES)[number];

/** Fixed-time duration in minutes (presets or a custom value from 1 to 48 hours). */
export type SiteDisarmFixedDurationMinutes = number;

/** Default Disarm until offset when Date & Time is selected. */
export const SITE_DISARM_DATE_TIME_DEFAULT_OFFSET_HOURS = 1;

/** ISO timestamp for `now + SITE_DISARM_DATE_TIME_DEFAULT_OFFSET_HOURS`. */
export function createDefaultSiteDisarmUntilAt(now: Date = new Date()): string {
  return new Date(
    now.getTime() + SITE_DISARM_DATE_TIME_DEFAULT_OFFSET_HOURS * 60 * 60 * 1000,
  ).toISOString();
}

export interface SiteDisarmReasonOption {
  readonly id: string;
  readonly label: string;
}

export interface SiteDisarmFixedDurationOption {
  readonly minutes: SiteDisarmFixedDurationPresetMinutes;
  readonly label: string;
}

export interface DisarmSiteConfirm {
  readonly reasonId: string;
  readonly disarmFor: SiteDisarmForOption;
  /** Required when `disarmFor` is `fixed-time`. */
  readonly durationMinutes?: SiteDisarmFixedDurationMinutes;
  /** ISO timestamp when `disarmFor` is `date-time` (must be within 48h of now). */
  readonly untilAt?: string;
}

export const SITE_DISARM_FOR_OPTIONS: ReadonlyArray<{
  readonly id: SiteDisarmForOption;
  readonly label: string;
}> = [
  { id: 'until-next-schedule', label: 'Until Next Schedule' },
  { id: 'fixed-time', label: 'Fixed Time Period' },
  { id: 'date-time', label: 'Date & Time' },
];

export const SITE_DISARM_FIXED_DURATION_OPTIONS: readonly SiteDisarmFixedDurationOption[] = [
  { minutes: 5, label: '5 minutes' },
  { minutes: 10, label: '10 minutes' },
  { minutes: 30, label: '30 minutes' },
  { minutes: 60, label: '60 minutes' },
] as const;

/** Parses a custom minutes field: positive integer from 1 through 48 hours. */
export function parseCustomDisarmDurationMinutes(raw: string): number | null {
  const trimmed = raw.trim();
  if (!/^\d+$/.test(trimmed)) {
    return null;
  }

  const minutes = Number(trimmed);
  if (minutes < 1 || minutes > SITE_DISARM_FIXED_DURATION_MAX_MINUTES) {
    return null;
  }

  return minutes;
}

/**
 * Value to bind into the popover. Retired presets (8h / 24h / 48h) are treated
 * as unselected unless Custom is active.
 */
export function resolveDisplayedDisarmFixedDurationMinutes(
  minutes: number | null | undefined,
  custom: boolean,
): number | null {
  if (minutes == null) {
    return null;
  }

  if (custom) {
    return parseCustomDisarmDurationMinutes(String(minutes));
  }

  return (SITE_DISARM_FIXED_DURATION_PRESET_MINUTES as readonly number[]).includes(minutes)
    ? minutes
    : null;
}
